import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, Trash2, Lock, ShieldCheck, RotateCcw, Tag, X, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createCheckout, validateCoupon } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (n) => `$${n.toFixed(2)}`;

const Cart = () => {
  const { items, addItem, updateQty, removeItem, total, clear } = useCart();
  const [email, setEmail] = useState(() => {
    // Prefill from `/cart?email=<value>` (e.g. /character hand-off) so the
    // buyer doesn't have to retype what they just typed on the landing page.
    try {
      return new URLSearchParams(window.location.search).get("email") || "";
    } catch { return ""; }
  });
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [coupon, setCoupon] = useState(null); // { code, promotion_code_id, discount_cents, discounted_total_cents, label }
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Guard against React.StrictMode double-invoke: only ever process a given
  // ?product=<id> once per mount, even if the effect fires twice.
  const handledProductRef = useRef(new Set());

  // Deep-link support: `/cart?product=<id>` auto-adds that product on load,
  // so any legacy link, bookmark, or email URL still works. Strips the param
  // after adding so refreshes don't double-add.
  useEffect(() => {
    const pid = searchParams.get("product");
    if (!pid) return;
    if (handledProductRef.current.has(pid)) return;
    handledProductRef.current.add(pid);
    if (items.some((i) => i.id === pid)) {
      const next = new URLSearchParams(searchParams);
      next.delete("product");
      setSearchParams(next, { replace: true });
      return;
    }
    axios.get(`${API}/products/${pid}`)
      .then(({ data }) => {
        addItem(data);
        toast.success(`${data.title} added to cart`);
      })
      .catch(() => toast.error("Couldn't find that product."))
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("product");
        setSearchParams(next, { replace: true });
      });
    // Intentionally run once per productId in the URL — addItem/items are stable enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("product")]);

  const subtotalCents = Math.round(total * 100);
  const discountCents = coupon ? Math.min(coupon.discount_cents, subtotalCents) : 0;
  const finalCents = Math.max(subtotalCents - discountCents, 0);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) { toast.error("Enter a promo code."); return; }
    setApplying(true);
    try {
      const res = await validateCoupon({ code, subtotal_cents: subtotalCents });
      setCoupon(res);
      toast.success(`${res.code} applied — ${res.label}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't apply that code.";
      toast.error(msg);
      setCoupon(null);
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!email) { toast.error("Please enter an email for delivery."); return; }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty })),
        customer_email: email,
        promotion_code_id: coupon?.promotion_code_id,
      });
      window.location.href = checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-4">Your cart is empty</h1>
        <p className="text-[#6B5E62] mb-8">Explore our ebooks and courses to get started.</p>
        <div className="flex justify-center gap-3">
          <Link to="/ebooks" className="btn-outline" data-testid="cart-empty-ebooks">Browse Ebooks</Link>
          <Link to="/courses" className="btn-primary" data-testid="cart-empty-courses">Browse Courses</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-8">Your cart</h1>
        <div className="divide-y divide-[#E8DCDD] border-t border-b border-[#E8DCDD]">
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-5 py-6">
              <img src={i.cover} alt={i.title} className="w-20 h-24 object-cover rounded-sm border border-[#E8DCDD]" />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B5E62]">{i.type === "ebook" ? "Ebook" : "Course"}</div>
                <div className="font-serif text-lg text-[#2D2628] leading-tight mt-1">{i.title}</div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex items-center border border-[#E8DCDD] rounded-sm">
                    <button onClick={() => updateQty(i.id, i.qty - 1)} data-testid={`cart-qty-decrement-${i.id}`}
                      className="w-8 h-8 flex items-center justify-center text-[#8C4A57] hover:bg-[#FAF4F5]"><Minus className="w-3 h-3" /></button>
                    <span className="px-3 text-sm min-w-8 text-center" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                    <button onClick={() => updateQty(i.id, i.qty + 1)} data-testid={`cart-qty-increment-${i.id}`}
                      className="w-8 h-8 flex items-center justify-center text-[#8C4A57] hover:bg-[#FAF4F5]"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`} className="text-xs text-[#6B5E62] hover:text-[#8C4A57] flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
              <div className="text-[#8C4A57] font-semibold">{currency(i.price * i.qty)}</div>
            </div>
          ))}
        </div>
        <button onClick={clear} data-testid="cart-clear" className="mt-6 text-xs text-[#6B5E62] hover:text-[#8C4A57] uppercase tracking-wider">
          Clear cart
        </button>
      </div>

      <aside className="lg:col-span-1">
        <div className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-md p-6 sticky top-28">
          <h2 className="font-serif text-xl text-[#2D2628] mb-4">Order summary</h2>
          <div className="flex justify-between text-sm text-[#6B5E62] mb-2">
            <span>Subtotal</span><span data-testid="cart-subtotal">{currency(total)}</span>
          </div>

          {/* Coupon input / applied state */}
          {!coupon ? (
            <div className="mb-4" data-testid="cart-coupon-block">
              <label className="block text-[10px] uppercase tracking-[0.16em] text-[#6B5E62] mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Promo code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  data-testid="cart-coupon-input"
                  placeholder="LEADER20"
                  className="flex-1 px-3 py-2 bg-white border border-[#E8DCDD] rounded-sm focus:ring-2 focus:ring-[#D9C5C9] focus:outline-none text-sm uppercase tracking-wide"
                />
                <button
                  onClick={applyCoupon}
                  disabled={applying || !couponInput.trim()}
                  data-testid="cart-coupon-apply"
                  className="px-4 py-2 bg-[#2D2628] text-white text-[11px] uppercase tracking-wider rounded-sm hover:bg-[#3E3336] disabled:opacity-50"
                >
                  {applying ? "…" : "Apply"}
                </button>
              </div>
            </div>
          ) : (
            <div
              data-testid="cart-coupon-applied"
              className="mb-4 flex items-center justify-between border border-[#8C4A57] bg-white rounded-sm px-3 py-2"
            >
              <div className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-[#8C4A57]" />
                <div>
                  <div className="text-[#2D2628] font-semibold" data-testid="cart-coupon-code">{coupon.code}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B5E62]" data-testid="cart-coupon-label">{coupon.label}</div>
                </div>
              </div>
              <button
                onClick={removeCoupon}
                data-testid="cart-coupon-remove"
                className="text-[#6B5E62] hover:text-[#8C4A57]"
                aria-label="Remove promo code"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {coupon && (
            <div className="flex justify-between text-sm text-[#8C4A57] mb-2" data-testid="cart-discount-row">
              <span>Discount ({coupon.code})</span>
              <span data-testid="cart-discount-amount">−{currency(discountCents / 100)}</span>
            </div>
          )}

          <div className="flex justify-between text-xs text-[#6B5E62] mb-4">
            <span>Tax</span><span>Calculated at checkout</span>
          </div>
          <div className="border-t border-[#E8DCDD] pt-4 flex justify-between text-base font-semibold text-[#2D2628] mb-6">
            <span>Total</span><span data-testid="cart-total">{currency(finalCents / 100)}</span>
          </div>
          <label className="block text-xs uppercase tracking-wider text-[#6B5E62] mb-2">Email for delivery</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="cart-email-input"
            className="w-full px-3 py-2 bg-white border border-[#E8DCDD] rounded-sm focus:ring-2 focus:ring-[#D9C5C9] focus:outline-none text-sm mb-4"
            placeholder="you@example.com" />
          <button onClick={checkout} disabled={loading} data-testid="cart-checkout-btn"
            className="w-full bg-[#8C4A57] text-white hover:bg-[#703B45] py-3 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors duration-200 shadow-sm disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />
            {loading ? "Redirecting..." : "Secure checkout"}
          </button>

          {/* 30-day money-back guarantee ribbon */}
          <div className="mt-4 flex items-start gap-3 border border-[#E8DCDD] rounded-sm bg-white px-4 py-3" data-testid="cart-guarantee-ribbon">
            <RotateCcw className="w-5 h-5 text-[#8C4A57] shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#8C4A57] font-semibold mb-1">
                30-day money-back guarantee
              </div>
              <p className="text-[11px] leading-relaxed text-[#6B5E62]">
                Not satisfied within 30 days? Email <a href="mailto:support@globalleadersolutions.com" className="text-[#8C4A57] hover:underline">support@globalleadersolutions.com</a> and we&apos;ll refund your full purchase — no questions asked.
              </p>
            </div>
          </div>

          {/* Secure-checkout trust strip */}
          <div className="mt-5 border border-[#E8DCDD] rounded-sm bg-[#FAF4F5] px-4 py-3" data-testid="cart-trust-strip">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[#8C4A57] shrink-0" />
              <div className="text-[11px] tracking-[0.16em] uppercase text-[#8C4A57] font-semibold">
                Secure checkout by Stripe
              </div>
            </div>
            <p className="text-[11px] leading-relaxed text-[#6B5E62] mb-3">
              Your card details are transmitted directly to Stripe over TLS 1.2+ and are never
              stored on our servers. Stripe is PCI DSS Level 1 certified — the highest standard
              in the payments industry.
            </p>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[10px] tracking-[0.14em] uppercase text-[#6B5E62] font-medium">
              <span>Visa</span><span>·</span>
              <span>Mastercard</span><span>·</span>
              <span>Amex</span><span>·</span>
              <span>Apple Pay</span><span>·</span>
              <span>Google Pay</span>
            </div>
          </div>

          <p className="text-[10px] text-[#6B5E62] mt-4 text-center leading-relaxed" data-testid="cart-descriptor-note">
            Digital delivery instantly after purchase. Your card statement will read{" "}
            <span className="text-[#2D2628] tracking-wider font-semibold">GLOBAL LEADER SOLUTIONS</span>.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default Cart;
